/* Mission feasibility.
 *
 * UI-UX-DESIGN.md §8: Command PE has estimation inside its Operation Planner but not on
 * ordinary missions. Telling an operator "this patrol is fuel-limited at 4h20m" while
 * they are setting it up prevents the most common planning error in the product.
 *
 * ── v0.9 HONESTY NOTE ────────────────────────────────────────────────────────
 * A transit-out / on-station / transit-back model at current speed. It ignores climb,
 * loiter speed differing from transit speed, reserves, alternates, and tanking. It is a
 * planning estimate and the UI says so. The kernel does this properly in v1.0.
 */
import { inverse, metresToNm } from './geodesy'
import type { OrbisEntity } from '../data/types'
import type { RefPoint } from '../data/refpoints'
import type { Mission } from '../data/missions'

export interface UnitFeasibility {
  entityId: string
  designation: string
  transitNm: number
  transitHours: number
  /** Hours the unit can hold on station after transit out and back. */
  onStationHours: number
  /** True if it can reach the area and return at all. */
  viable: boolean
}

export interface MissionFeasibility {
  units: UnitFeasibility[]
  /** Best single-unit time on station. */
  bestOnStationHours: number
  /**
   * Total coverage if units rotate through the station one at a time.
   * The honest headline number for a patrol.
   */
  totalCoverageHours: number
  /** Can the requested number of units actually be maintained on station? */
  canSustainRequested: boolean
  centroid: { lon: number; lat: number } | null
  note: string
}

function centroidOf(mission: Mission, refPoints: RefPoint[]): { lon: number; lat: number } | null {
  const pts = mission.areaRefPoints
    .map((id) => refPoints.find((r) => r.id === id))
    .filter((r): r is RefPoint => !!r)
  if (pts.length === 0) return null
  return {
    lon: pts.reduce((a, p) => a + p.lon, 0) / pts.length,
    lat: pts.reduce((a, p) => a + p.lat, 0) / pts.length,
  }
}

export function assessMission(
  mission: Mission,
  entities: OrbisEntity[],
  refPoints: RefPoint[],
): MissionFeasibility {
  const centroid = centroidOf(mission, refPoints)
  const assigned = mission.assigned
    .map((id) => entities.find((e) => e.id === id))
    .filter((e): e is OrbisEntity => !!e)

  if (!centroid) {
    return {
      units: [],
      bestOnStationHours: 0,
      totalCoverageHours: 0,
      canSustainRequested: false,
      centroid: null,
      note: 'No area defined — bind reference points to this mission.',
    }
  }

  const units: UnitFeasibility[] = assigned.map((e) => {
    const speed = Math.max(e.speedKt, 1)
    const enduranceLeft = (e.enduranceHrs ?? 0) * e.fuelFraction
    const transitNm = metresToNm(inverse({ lon: e.lon, lat: e.lat }, centroid).distanceM)
    const transitHours = transitNm / speed
    // Out and back. A unit that cannot get home is not on station, it is lost.
    const onStationHours = enduranceLeft - transitHours * 2
    return {
      entityId: e.id,
      designation: e.designation,
      transitNm,
      transitHours,
      onStationHours,
      viable: onStationHours > 0,
    }
  })

  const viable = units.filter((u) => u.viable)
  const bestOnStationHours = viable.length ? Math.max(...viable.map((u) => u.onStationHours)) : 0
  const totalCoverageHours = viable.reduce((a, u) => a + u.onStationHours, 0)

  // Sustaining N units simultaneously divides the pooled coverage N ways.
  const requested = Math.max(mission.onStation, 1)
  const canSustainRequested = viable.length >= requested && totalCoverageHours / requested > 0

  let note: string
  if (assigned.length === 0) note = 'No units assigned.'
  else if (viable.length === 0) note = 'No assigned unit can reach the area and return.'
  else if (viable.length < requested)
    note = `Only ${viable.length} of ${requested} requested units can hold station.`
  else note = 'Sustainable with the assigned force.'

  return {
    units,
    bestOnStationHours,
    totalCoverageHours,
    canSustainRequested,
    centroid,
    note,
  }
}
